import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader, LoadingState, EmptyState, ErrorState } from "@/components/features/states";
import { toast } from "sonner";
import { Megaphone, Plus, Pencil, Trash2, Pause, Play } from "lucide-react";
import type { Advertisement } from "@/lib/types";
import { COUNTRIES } from "@/lib/countries";
import { useTranslation } from "react-i18next";

const VERTICAL_OPTIONS = ["all", "parcel", "food", "grocery", "pharmacy", "other"];
const STATUS_OPTIONS: Advertisement["status"][] = ["draft", "active", "paused", "expired"];
const PRICING_OPTIONS: Advertisement["pricingModel"][] = ["cpm", "cpc", "flat"];

interface AdForm {
  title: string;
  description: string;
  imageUrl: string;
  targetUrl: string;
  ctaLabel: string;
  vertical: string;
  countryCode: string; // "all" or "+253" etc.
  status: Advertisement["status"];
  pricingModel: Advertisement["pricingModel"];
  budget: string;
  endsAt: string;
}

const EMPTY_FORM: AdForm = {
  title: "",
  description: "",
  imageUrl: "",
  targetUrl: "",
  ctaLabel: "Learn more",
  vertical: "all",
  countryCode: "all",
  status: "active",
  pricingModel: "cpc",
  budget: "",
  endsAt: "",
};

// statusBadge returns a styled badge. The status label itself is rendered as
// the raw status code (e.g. "active", "paused") per the existing UX — no i18n key.
function statusBadge(status: Advertisement["status"]) {
  const map: Record<string, string> = {
    draft: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    paused: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    expired: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${map[status] ?? ""}`}>
      {status}
    </span>
  );
}

export function AdminAds() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Advertisement | null>(null);
  const [form, setForm] = useState<AdForm>(EMPTY_FORM);
  const [deleting, setDeleting] = useState<Advertisement | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "ads"],
    queryFn: () => api.admin.ads(),
    refetchInterval: 30000,
  });
  const ads = data?.data?.ads ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "ads"] });
    qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    qc.invalidateQueries({ queryKey: ["admin", "analytics"] });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (ad: Advertisement) => {
    setEditing(ad);
    setForm({
      title: ad.title || "",
      description: ad.description || "",
      imageUrl: ad.imageUrl || "",
      targetUrl: ad.targetUrl || "",
      ctaLabel: ad.ctaLabel || "Learn more",
      vertical: ad.vertical || "all",
      countryCode: ad.countryCode || "all",
      status: ad.status,
      pricingModel: ad.pricingModel,
      budget: ad.budget != null ? String(ad.budget) : "",
      endsAt: ad.endsAt ? ad.endsAt.slice(0, 10) : "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error(t("admin.ads.titleRequired"));
    setSaving(true);
    const body: Partial<Advertisement> = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      imageUrl: form.imageUrl.trim() || null,
      targetUrl: form.targetUrl.trim() || null,
      ctaLabel: form.ctaLabel.trim() || t("admin.ads.defaultCta"),
      vertical: form.vertical,
      countryCode: form.countryCode === "all" ? null : form.countryCode,
      status: form.status,
      pricingModel: form.pricingModel,
      budget: form.budget ? parseFloat(form.budget) : null,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
    };
    const res = editing
      ? await api.admin.updateAd(editing.id, body)
      : await api.admin.createAd(body);
    setSaving(false);
    if (!res.ok) return toast.error(res.error || t("admin.ads.adSavedFailed"));
    toast.success(editing ? t("admin.ads.adUpdatedToast") : t("admin.ads.adCreatedToast"));
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    invalidate();
  };

  const toggleStatus = async (ad: Advertisement) => {
    const next: Advertisement["status"] = ad.status === "active" ? "paused" : "active";
    const res = await api.admin.updateAd(ad.id, { status: next });
    if (!res.ok) return toast.error(res.error);
    toast.success(next === "active" ? t("admin.ads.adActivatedToast") : t("admin.ads.adPausedToast"));
    invalidate();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const res = await api.admin.deleteAd(deleting.id);
    if (!res.ok) return toast.error(res.error || t("admin.ads.adDeleteFailed"));
    toast.success(t("admin.ads.adDeletedToast"));
    setDeleting(null);
    invalidate();
  };

  // Form is reset by openCreate / openEdit before each open, so no
  // reset-on-close effect is needed.

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.ads.loadAdsFailed")} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("admin.ads.title")}
        subtitle={t("admin.ads.subtitle", { count: ads.length })}
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" /> {t("admin.ads.createAd")}
          </Button>
        }
      />

      {ads.length === 0 ? (
        <EmptyState
          icon={<Megaphone className="h-8 w-8" />}
          title={t("admin.ads.noAdsYet")}
          description={t("admin.ads.noAdsDesc")}
          action={
            <Button size="sm" onClick={openCreate}>
              <Plus className="mr-1 h-4 w-4" /> {t("admin.ads.createAd")}
            </Button>
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.ads.colTitle")}</TableHead>
                <TableHead>{t("admin.ads.colVertical")}</TableHead>
                <TableHead>{t("admin.ads.colStatus")}</TableHead>
                <TableHead className="text-right">{t("admin.ads.colImpr")}</TableHead>
                <TableHead className="text-right">{t("admin.ads.colClicks")}</TableHead>
                <TableHead className="text-right">{t("admin.ads.colCtr")}</TableHead>
                <TableHead className="text-right">{t("admin.ads.colBudget")}</TableHead>
                <TableHead className="text-right">{t("admin.ads.colSpent")}</TableHead>
                <TableHead className="text-right">{t("admin.ads.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ads.map((ad) => {
                const ctr = ad.impressions > 0 ? (ad.clicks / ad.impressions) * 100 : 0;
                return (
                  <TableRow key={ad.id}>
                    <TableCell className="max-w-[200px]">
                      <div className="flex items-center gap-2">
                        {ad.imageUrl && (
                          <img src={ad.imageUrl} alt="" className="h-8 w-8 rounded object-cover" loading="lazy" />
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{ad.title}</p>
                          {ad.countryCode && (
                            <p className="text-[10px] text-muted-foreground">{ad.countryCode}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{ad.vertical}</TableCell>
                    <TableCell>{statusBadge(ad.status)}</TableCell>
                    <TableCell className="text-right">{ad.impressions}</TableCell>
                    <TableCell className="text-right">{ad.clicks}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{ctr.toFixed(1)}%</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {ad.budget != null ? ad.budget : "—"}
                    </TableCell>
                    <TableCell className="text-right">{ad.spent}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleStatus(ad)}
                          aria-label={ad.status === "active" ? t("admin.ads.pauseAria") : t("admin.ads.activateAria")}
                          title={ad.status === "active" ? t("admin.ads.pauseTitle") : t("admin.ads.activateTitle")}
                        >
                          {ad.status === "active" ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(ad)}
                          aria-label={t("admin.ads.editAria")}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setDeleting(ad)}
                          aria-label={t("admin.ads.deleteAria")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? t("admin.ads.editAd") : t("admin.ads.createAd")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ad-title">{t("admin.ads.titleLabel")}</Label>
              <Input
                id="ad-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t("admin.ads.titlePlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ad-desc">{t("admin.ads.descriptionLabel")}</Label>
              <Textarea
                id="ad-desc"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t("admin.ads.descriptionPlaceholder")}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ad-img">{t("admin.ads.imageUrlLabel")}</Label>
                <Input
                  id="ad-img"
                  value={form.imageUrl}
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  placeholder={t("admin.ads.imageUrlPlaceholder")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ad-cta">{t("admin.ads.ctaLabelField")}</Label>
                <Input
                  id="ad-cta"
                  value={form.ctaLabel}
                  onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ad-target">{t("admin.ads.targetUrlLabel")}</Label>
              <Input
                id="ad-target"
                value={form.targetUrl}
                onChange={(e) => setForm({ ...form, targetUrl: e.target.value })}
                placeholder={t("admin.ads.targetUrlPlaceholder")}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ad-vertical">{t("admin.ads.verticalLabel")}</Label>
                <select
                  id="ad-vertical"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.vertical}
                  onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                >
                  {VERTICAL_OPTIONS.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ad-country">{t("admin.ads.countryLabel")}</Label>
                <select
                  id="ad-country"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.countryCode}
                  onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                >
                  <option value="all">{t("admin.ads.countryAll")}</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ad-status">{t("admin.ads.statusLabel")}</Label>
                <select
                  id="ad-status"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm capitalize"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as Advertisement["status"] })}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ad-pricing">{t("admin.ads.pricingLabel")}</Label>
                <select
                  id="ad-pricing"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.pricingModel}
                  onChange={(e) => setForm({ ...form, pricingModel: e.target.value as Advertisement["pricingModel"] })}
                >
                  {PRICING_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ad-budget">{t("admin.ads.budgetLabel")}</Label>
                <Input
                  id="ad-budget"
                  type="number"
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  placeholder={t("admin.ads.budgetPlaceholder")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ad-ends">{t("admin.ads.endsAtLabel")}</Label>
                <Input
                  id="ad-ends"
                  type="date"
                  value={form.endsAt}
                  onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("admin.ads.cancel")}</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? t("admin.ads.saving") : editing ? t("admin.ads.updateAd") : t("admin.ads.createAd")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.ads.deleteAdTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("admin.ads.deleteAdDesc", { title: deleting?.title })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>{t("admin.ads.cancel")}</Button>
            <Button variant="destructive" onClick={confirmDelete}>{t("common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
